<script setup>
import {ref, computed, watch, onMounted, onBeforeUnmount} from 'vue'
import {Editor, EditorContent} from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Paragraph from '@tiptap/extension-paragraph'
import Link from '@tiptap/extension-link'
import TextAlign from '@tiptap/extension-text-align'
import Heading from '@tiptap/extension-heading'
import Image from '@tiptap/extension-image'
import {TextStyle} from '@tiptap/extension-text-style'
import Color from '@tiptap/extension-color'
import Highlight from '@tiptap/extension-highlight'
import BulletList from '@tiptap/extension-bullet-list'
import ListItem from '@tiptap/extension-list-item'
import {NodeSelection} from '@tiptap/pm/state'
import {useAppStore} from '@/stores/appStore'
import {Table} from '@tiptap/extension-table'
import {TableRow} from '@tiptap/extension-table-row'
import {TableCell} from '@tiptap/extension-table-cell'
import {TableHeader} from '@tiptap/extension-table-header'
import {mergeAttributes} from '@tiptap/core'
import HardBreak from '@tiptap/extension-hard-break'

// Local Components
import TiptapToolbar from "./TiptapToolbar.vue"
import TipTapImageSelectionModal from "@/vue/modals/TipTapImageSelectionModal.vue"
import TiptapFooter from "./TiptapFooter.vue"

const model = defineModel({ type: String, default: '' })

const props = defineProps({
    formId: {type: String, required: true}
})

const appStore = useAppStore()

const editor = ref(null)
const isSourceMode = ref(false)
const isFullScreen = ref(false)
const rawHtml = ref('')
const showImageModal = ref(false)
const activeImageAttrs = ref(null)
const pasteAsPlainTextActive = ref(false)
const isTouched = ref(false)

const cssFramework = computed(() => appStore.settings?.config?.css_framework || 'bootstrap@3.3.7')
const isBootstrap = computed(() => cssFramework.value.startsWith('bootstrap'))
const apiHost = computed(() => appStore.ccmsSchemeAndHost)

// Keep Tiptap's non-reactive DOM updated when Pinia/Parent state changes externally
watch(model, (newValue) => {
    if (!editor.value) return
    if (editor.value.getHTML() === newValue) return

    editor.value.commands.setContent(newValue, false)
})

const formatHtml = (html) => {
    let tab = '  '
    let result = ''
    let indent = ''
    html.split(/>\s*</).forEach((element) => {
        if (element.match(/^\/\w/)) indent = indent.substring(tab.length)
        result += indent + '<' + element + '>\n\n'
        if (element.match(/^<?\w[^>]*[^\/]$/) && !element.startsWith("input") && !element.startsWith("img") && !element.startsWith("br")) {
            indent += tab
        }
    })
    return result.substring(1, result.length - 3)
}

const toggleSource = () => {
    if (isSourceMode.value) {
        // Set content and trigger model sync
        editor.value.commands.setContent(rawHtml.value, true)
        model.value = rawHtml.value
    } else {
        rawHtml.value = formatHtml(editor.value.getHTML())
    }
    isSourceMode.value = !isSourceMode.value
}

const openImageModal = () => {
    const {selection} = editor.value.state
    if (selection.node && selection.node.type.name === 'image') {
        activeImageAttrs.value = selection.node.attrs
    }
    showImageModal.value = true
}

const closeImageModal = () => {
    showImageModal.value = false
    activeImageAttrs.value = null
}

onMounted(() => {
    const blockquoteOptions = isBootstrap.value ? {HTMLAttributes: {class: 'blockquote'}} : {}

    // 1. Custom Table to strip <colgroup> and default style attributes
    const CustomTable = Table.extend({
        renderHTML({HTMLAttributes}) {
            const {style, ...restAttributes} = HTMLAttributes
            return [
                'table',
                mergeAttributes(this.options.HTMLAttributes, restAttributes),
                ['tbody', 0]
            ]
        }
    })

    // 2. Custom TableCell and TableHeader allow inline content and clean attributes
    const cleanCellAttributes = {
        //content: 'inline*', // Strips out the <p> but prevents line breaks being added to cells
        addAttributes() {
            return {
                colspan: {
                    default: 1,
                    renderHTML: attributes => (attributes.colspan <= 1 ? {} : {colspan: attributes.colspan}),
                },
                rowspan: {
                    default: 1,
                    renderHTML: attributes => (attributes.rowspan <= 1 ? {} : {rowspan: attributes.rowspan}),
                },
            }
        },
    }

    // 3. Custom HardBreak handling Enter key inside cells
    const CustomHardBreak = HardBreak.extend({
        addKeyboardShortcuts() {
            return {
                'Shift-Enter': () => this.editor.commands.setHardBreak(),
                'Enter': ({editor}) => {
                    if (editor.isActive('tableCell') || editor.isActive('tableHeader')) {
                        return editor.commands.setHardBreak()
                    }
                    return false
                },
            }
        },
    })

    editor.value = new Editor({
        content: model.value,
        autofocus: false,
        extensions: [
            StarterKit.configure({
                paragraph: false,
                blockquote: blockquoteOptions,
                bulletList: false,
                listItem: false,
                // I believe this was added in trying to get lists to support headings and paragraphs together
                // as block elements - see ListItem below - but it doesn't work. This had the knock-on of breaking
                // all headings so has been disabled.
                //heading: false,
                link: false,
                hardBreak: false
            }),
            Paragraph.extend({
                addAttributes() {
                    return {class: {default: null}}
                }
            }),
            BulletList.extend({
                addAttributes() {
                    return {
                        class: {
                            default: null,
                            parseHTML: el => el.getAttribute('class'),
                            renderHTML: attrs => attrs.class ? {class: attrs.class} : {}
                        }
                    }
                }
            }),
            ListItem.extend({
                content: '(paragraph|heading) block*',
                addKeyboardShortcuts() {
                    return {
                        'Enter': () => this.editor.commands.splitListItem(this.name),
                        'Shift-Enter': () => this.editor.commands.splitBlock(),
                    }
                },
            }),
            TextStyle, Color,
            Highlight.configure({multicolor: true}),
            TextAlign.configure({types: ['heading', 'paragraph']}),
            Image.extend({
                inline: true,
                group: 'inline',
                addAttributes() {
                    return {
                        src: {
                            parseHTML: el => el.getAttribute('src'),
                            renderHTML: attrs => {
                                let src = attrs.src
                                if (src && src.startsWith(apiHost.value)) src = src.replace(apiHost.value, '')
                                return {src}
                            }
                        },
                        alt: {default: null},
                        title: {default: null},
                        width: {default: null},
                        height: {default: null},
                        sizes: {
                            default: null,
                            renderHTML: attrs => attrs.sizes ? {sizes: attrs.sizes} : {}
                        },
                        class: {default: null},
                        style: {
                            default: null,
                            parseHTML: el => {
                                const inline = el.getAttribute('style')
                                if (inline) return inline
                                const cls = el.getAttribute('class') || ''
                                if (cls.includes('text-editor-image-left')) return 'display: block; max-width: 100%; float: left; margin: 0 1rem 1rem 0;'
                                if (cls.includes('text-editor-image-right')) return 'display: block; max-width: 100%; float: right; margin: 0 0 1rem 1rem;'
                                if (cls.includes('text-editor-image-center')) return 'display: block; max-width: 100%; margin: 0 auto 1rem auto;'
                                return null
                            },
                            renderHTML: attrs => attrs.style ? {style: attrs.style} : {}
                        }
                    }
                },
                addNodeView() {
                    return ({node, view, getPos}) => {
                        const img = document.createElement('img')

                        img.addEventListener('click', (event) => {
                            event.preventDefault()
                            event.stopPropagation()

                            if (typeof getPos === 'function') {
                                const {state, dispatch} = view
                                const transaction = state.tr.setSelection(
                                    NodeSelection.create(state.doc, getPos())
                                )
                                dispatch(transaction)
                            }
                        })

                        const sync = (attrs) => {
                            Object.entries(attrs).forEach(([key, value]) => {
                                if (value === null || value === undefined) {
                                    img.removeAttribute(key)
                                    return
                                }
                                let finalValue = value
                                if (key === 'src' && !value.startsWith('http')) finalValue = apiHost.value + value
                                if (img.getAttribute(key) !== String(finalValue)) img.setAttribute(key, finalValue)
                            })
                        }

                        sync(node.attrs)

                        return {
                            dom: img,
                            update: (updatedNode) => {
                                if (updatedNode.type.name !== 'image') return false
                                sync(updatedNode.attrs)
                                return true
                            }
                        }
                    }
                }
            }),
            Heading.extend({
                addAttributes() {
                    return {
                        class: {
                            default: null,
                            parseHTML: el => el.getAttribute('class'),
                            renderHTML: attrs => attrs.class ? {class: attrs.class} : {}
                        }
                    }
                }
            }).configure({levels: [1, 2, 3, 4, 5, 6]}),
            Link.extend({
                addAttributes() {
                    return {
                        ...this.parent?.(),
                        class: {default: null},
                        target: {
                            default: null,
                            renderHTML: attributes => {
                                const href = attributes.href || ''
                                const isInternal = isInternalUrl(href)
                                return isInternal
                                    ? {target: null}
                                    : {target: '_blank'}
                            }
                        },
                        rel: {
                            default: null,
                            renderHTML: attributes => {
                                const href = attributes.href || ''
                                const isInternal = isInternalUrl(href)
                                return isInternal ? {rel: 'noopener'} : {rel: 'noopener noreferrer nofollow'}
                            }
                        }
                    }
                }
            }).configure({
                openOnClick: false,
                autolink: true,
                linkOnPaste: true,
                validate: href => !!href,
                HTMLAttributes: {}
            }),
            // Register the table elements so Tiptap recognizes and preserves the HTML
            // This has been added so clients can paste HTML tables into editors, but mainly to preserve existing
            // tables in BBB.
            CustomTable.configure({
                resizable: false, // Disables table column resizing handles
            }),
            TableRow,
            TableCell.extend(cleanCellAttributes),
            TableHeader.extend(cleanCellAttributes),
            HardBreak
        ],
        editorProps: {
            attributes: {class: 'editor-content'},
            handleKeyDown: (view, e) => {
                if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'u') {
                    e.preventDefault()
                    return true
                }
                return false
            },
            handlePaste: (view, event) => {
                if (!pasteAsPlainTextActive.value) {
                    return false
                }

                let text = event.clipboardData.getData('text/plain')

                if (text) {
                    const cleanText = text.replace(/\u200B/g, "").trim()

                    const {state, dispatch} = view
                    const {from, to} = state.selection

                    const transaction = state.tr.insertText(cleanText, from, to)
                    dispatch(transaction)

                    return true
                }

                return false
            },
            handleDOMEvents: {
                dblclick: (view, event) => {
                    if (event.target?.nodeName === 'IMG') {
                        openImageModal()
                        return true
                    }
                    return false
                }
            }
        },
        onFocus: () => {
            if (!isTouched.value) isTouched.value = true
        },
        onCreate: ({editor: currentEditor}) => {
            const domElement = currentEditor.options.element
            if (!domElement) return

            const killLinkNavigation = (event) => {
                const target = event.target
                const closestLink = target?.closest('a')

                if (closestLink) {
                    event.preventDefault()
                    event.stopPropagation()

                    if (event.type === 'click' && target && target.nodeName === 'IMG') {
                        const view = currentEditor.view
                        const pos = view.posAtDOM(target, 0)
                        if (pos !== undefined && pos >= 0) {
                            const {state, dispatch} = view
                            const transaction = state.tr.setSelection(NodeSelection.create(state.doc, pos))
                            dispatch(transaction)
                        }
                    }
                }
            }

            domElement.addEventListener('click', killLinkNavigation, {capture: true})
            domElement.addEventListener('pointerup', killLinkNavigation, {capture: true})
            domElement.addEventListener('mouseup', killLinkNavigation, {capture: true})
        },
        onUpdate: () => {
            const html = editor.value.getHTML()
            const host = apiHost.value
            const escapedHost = host.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
            model.value = html.replace(new RegExp(escapedHost, 'g'), '')//.replace(/<p><\/p>$/, '')
        }
    })
})

onBeforeUnmount(() => {
    if (editor.value) {
        editor.value.commands.blur()

        const instance = editor.value
        editor.value = null

        setTimeout(() => {
            try {
                instance.destroy()
            } catch (e) {
                console.warn('Tiptap cleanup handled safely.')
            }
        }, 0)
    }
})

// Helper function to check if a URL is internal
function isInternalUrl(href) {
    if (!href) return false

    // Relative links and placeholder tokens
    if (
        href.startsWith('/') ||
        href.startsWith('#') ||
        href.startsWith('[PID:') ||
        href.startsWith('[CID:')
    ) {
        return true
    }

    return false
}
</script>

<template>
    <div v-if="editor" class="editor-wrapper" :class="{'tiptap-fullscreen': isFullScreen}">
        <TiptapToolbar
            v-if="isTouched"
            v-model:paste-as-plain-text-active="pasteAsPlainTextActive"
            :editor="editor"
            :is-full-screen="isFullScreen"
            :is-source-mode="isSourceMode"
            @toggle-source="toggleSource"
            @toggle-full-screen="isFullScreen = !isFullScreen"
            @open-image-modal="openImageModal"
        />

        <editor-content v-show="!isSourceMode" :editor="editor" class="flex-fill overflow-y-auto"/>

        <TiptapFooter v-if="isTouched" v-show="!isSourceMode" :editor="editor"/>

        <textarea
            v-show="isSourceMode"
            v-model="rawHtml"
            class="form-control source-mode-textarea small flex-fill"
        />

        <TipTapImageSelectionModal
            v-if="showImageModal"
            :form-id="formId"
            :editor="editor"
            :image="activeImageAttrs"
            @hide-modal="closeImageModal"
        />
    </div>
</template>

<style scoped lang="scss">
$editor-primary: #0d6efd;
$editor-border: var(--bs-border-color);

.editor-wrapper {
    position: relative;
    border: 1px solid $editor-border;
    border-radius: 0.375rem;
    background-color: #fff;

    :deep(.tiptap-toolbar) {
        position: sticky;
        // This was disabled because it caused top margin in Safari but
        // without it sticky doesn't work.
        top: 0;
        z-index: 10;
    }

    :deep(.ProseMirror) {
        padding: 0.75rem;
        min-height: 200px;
        outline: none !important;

        a {
            pointer-events: none;
            cursor: text;
        }

        img {
            outline: 6px solid transparent;
            outline-offset: -6px;
            display: inline-block;
            transition: outline 0.1s ease-in-out;
            pointer-events: auto;

            &.ProseMirror-selectednode {
                outline: 3px solid $editor-primary !important;
                outline-offset: 2px;
            }
        }

        tr, th, td {
            border: 1px dashed #eaeaea;
        }
    }

    :deep(.source-mode-textarea) {
        display: block;
        width: 100%;
        padding: 0.75rem;
        background-color: #f8f9fa;
        font-family: monospace;
        min-height: 200px;
        font-size: 13px;
        field-sizing: content;
        resize: none;
    }

    &.tiptap-fullscreen {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 1050;
        display: flex;
        flex-direction: column;
        background: #fff;

        // Top is required for sticky to work above, but it creates an erroneous
        // top margin in Safari when in fullscreen mode.
        :deep(.tiptap-toolbar) {
            top: auto;
        }
    }
}

[data-bs-theme="dark"] {
    .editor-wrapper {
        background-color: black;

        :deep(.tiptap-toolbar) {
            background: #000;
        }

        :deep(.source-mode-textarea) {
            background-color: #000;
        }
    }
}
</style>